/**
 * The room is two flat surfaces: a floor (y = 0) and a wall (z = 0), meeting
 * along the wall's foot. Each surface is drawn as rectangles: one large core
 * rectangle and, around its left, right and far edges, a thin ring of five
 * rectangles whose outer vertices have alpha 0. With vertex alpha driving
 * opacity, the ring fades the room out at its edges.
 *
 * Every vertex also gets uv0 in metres in the room entity's space, so a
 * material's tiling is repeats per metre and the pattern holds still as the
 * room grows, whatever the tiling; and uv1 with v
 * going from 1 at the wall's foot to 0 at the shadow height for the artist's
 * contact-shadow texture.
 */

export type RoomSize = {
	width: number;
	depth: number;
	height: number;
};

export type RoomLayout = RoomSize & {
	/** Offset of the room's centre along x, in the room entity's local space. */
	centerX: number;
};

export type RoomGeometryParams = RoomLayout & {
	ringWidth: number;
	shadowHeight: number;
};

export type MeshData = {
	positions: number[];
	normals: number[];
	uvs: number[];
	uvs1: number[];
	colors: number[];
	indices: number[];
};

export type RoomGeometry = {
	floorCore: MeshData;
	floorRing: MeshData;
	wallCore: MeshData;
	wallRing: MeshData;
};

export type RoomSizingOptions = {
	tileSize: number;
	floorMarginTiles: number;
	wallMarginTopTiles: number;
	minDepthTiles: number;
};

type Vec3Like = { x: number; y: number; z: number };

/**
 * The footprint snaps outward to whole tiles with a margin on each side, the
 * wall is pinned at z = 0 and the wall height follows the bounds continuously.
 */
export function layoutFromLocalBounds(
	min: Vec3Like,
	max: Vec3Like,
	options: RoomSizingOptions,
): RoomLayout {
	const { tileSize, floorMarginTiles, wallMarginTopTiles, minDepthTiles } =
		options;
	const minX = Math.min(min.x, max.x);
	const maxX = Math.max(min.x, max.x);
	const maxY = Math.max(min.y, max.y);
	const maxZ = Math.max(min.z, max.z);

	const leftTile = Math.floor(minX / tileSize) - floorMarginTiles;
	const rightTile = Math.ceil(maxX / tileSize) + floorMarginTiles;
	const frontTile = Math.max(
		Math.ceil(maxZ / tileSize) + floorMarginTiles,
		minDepthTiles,
	);
	const topTile = maxY / tileSize + wallMarginTopTiles;

	return {
		width: (rightTile - leftTile) * tileSize,
		depth: frontTile * tileSize,
		height: topTile * tileSize,
		centerX: ((leftTile + rightTile) / 2) * tileSize,
	};
}

export function layoutsAreEqual(a: RoomLayout, b: RoomLayout): boolean {
	return (
		a.width === b.width &&
		a.depth === b.depth &&
		a.height === b.height &&
		a.centerX === b.centerX
	);
}

/**
 * A rectangle on a surface. `left`/`right` are x. `near`/`far` are the
 * surface's other axis: z on the floor, y on the wall, with `near` at the
 * wall's foot.
 */
type Rect = { left: number; right: number; near: number; far: number };

type Vertex = {
	position: [number, number, number];
	uv0: [number, number];
	uv1: [number, number];
	alpha: number;
};

type Surface = {
	normal: [number, number, number];
	/** Listing a rectangle's corners left-near, right-near, right-far, left-far winds clockwise on this surface. */
	windsClockwise: boolean;
	vertex(x: number, farAxis: number): Vertex;
};

export function createRoomGeometry(params: RoomGeometryParams): RoomGeometry {
	const { width, depth, height, centerX, ringWidth, shadowHeight } = params;
	const left = centerX - width / 2;
	const right = centerX + width / 2;

	const alphaAt = (x: number, farAxis: number, farEdge: number) =>
		x === left || x === right || farAxis === farEdge ? 0 : 1;

	const floor: Surface = {
		normal: [0, 1, 0],
		windsClockwise: true,
		vertex: (x, z) => ({
			position: [x, 0, z],
			uv0: [x, z],
			uv1: [x, 1 - z / shadowHeight],
			alpha: alphaAt(x, z, depth),
		}),
	};
	const wall: Surface = {
		normal: [0, 0, 1],
		windsClockwise: false,
		vertex: (x, y) => ({
			position: [x, y, 0],
			uv0: [x, y],
			uv1: [x, 1 - y / shadowHeight],
			alpha: alphaAt(x, y, height),
		}),
	};

	return {
		floorCore: buildMesh(floor, [coreRect(left, right, depth, ringWidth)]),
		floorRing: buildMesh(floor, ringRects(left, right, depth, ringWidth)),
		wallCore: buildMesh(wall, [coreRect(left, right, height, ringWidth)]),
		wallRing: buildMesh(wall, ringRects(left, right, height, ringWidth)),
	};
}

function coreRect(left: number, right: number, far: number, r: number): Rect {
	return { left: left + r, right: right - r, near: 0, far: far - r };
}

/** The band, r wide, along the left, right and far edges of the surface. */
function ringRects(
	left: number,
	right: number,
	far: number,
	r: number,
): Rect[] {
	return [
		{ left, right: left + r, near: 0, far: far - r },
		{ left: right - r, right, near: 0, far: far - r },
		{ left: left + r, right: right - r, near: far - r, far },
		{ left, right: left + r, near: far - r, far },
		{ left: right - r, right, near: far - r, far },
	];
}

/** Two triangles per rectangle, wound counter-clockwise so they face the surface normal. */
function buildMesh(surface: Surface, rects: Rect[]): MeshData {
	const mesh: MeshData = {
		positions: [],
		normals: [],
		uvs: [],
		uvs1: [],
		colors: [],
		indices: [],
	};

	for (const rect of rects) {
		const first = mesh.positions.length / 3;
		const corners = [
			[rect.left, rect.near],
			[rect.right, rect.near],
			[rect.right, rect.far],
			[rect.left, rect.far],
		];
		if (surface.windsClockwise) {
			corners.reverse();
		}
		for (const [x, farAxis] of corners) {
			const vertex = surface.vertex(x, farAxis);
			mesh.positions.push(...vertex.position);
			mesh.normals.push(...surface.normal);
			mesh.uvs.push(...vertex.uv0);
			mesh.uvs1.push(...vertex.uv1);
			mesh.colors.push(1, 1, 1, vertex.alpha);
		}
		mesh.indices.push(first, first + 1, first + 2, first, first + 2, first + 3);
	}

	return mesh;
}
